import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../../contexts/AdminAuthContext";

const AdminLoginPage = () => {
    const { user, loading, login } = useAdminAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        document.title = "Admin Login — Raj Graphics";
    }, []);

    if (!loading && user) {
        const from = location.state?.from?.pathname || "/admin/dashboard";
        return <Navigate to={from} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setErrMsg("");
        if (!username.trim() || !password) {
            setErrMsg("Enter both username and password.");
            return;
        }
        setSubmitting(true);
        try {
            await login(username.trim(), password);
            toast.success("Welcome back");
            navigate("/admin/dashboard", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || err.message || "Login failed";
            setErrMsg(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid="admin-login-page"
            className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-16 relative overflow-hidden"
        >
            <div className="absolute inset-0 grid-pattern radial-fade opacity-50 pointer-events-none" />
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />

            <div className="relative w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 rounded-full bg-gold/10 border border-gold/30 px-4 py-1.5 mb-6">
                        <ShieldCheck size={13} className="text-gold" />
                        <span className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                            Studio Admin
                        </span>
                    </div>
                    <h1 className="font-poppins font-bold text-white text-3xl sm:text-4xl tracking-tight">
                        Sign in to <span className="gold-gradient-text">Raj Graphics</span>
                    </h1>
                    <p className="mt-3 font-inter text-sm text-zinc-400">
                        Enter your admin credentials to manage the website.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    noValidate
                    data-testid="admin-login-form"
                    className="bg-white/[0.02] border border-white/10 rounded-2xl p-7 md:p-9 space-y-5"
                >
                    <div>
                        <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                            Username
                        </label>
                        <input
                            type="text"
                            autoComplete="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            data-testid="admin-input-username"
                            placeholder="rajgraphics"
                            className="bg-black/40 border border-white/10 focus:border-gold rounded-lg px-4 py-3 text-white outline-none w-full transition-colors font-inter"
                        />
                    </div>

                    <div>
                        <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                            Password
                        </label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-input-password"
                            placeholder="••••••••"
                            className="bg-black/40 border border-white/10 focus:border-gold rounded-lg px-4 py-3 text-white outline-none w-full transition-colors font-inter"
                        />
                    </div>

                    {errMsg && (
                        <p
                            data-testid="admin-login-error"
                            className="text-sm text-red-400 font-inter bg-red-400/5 border border-red-400/20 rounded-lg px-4 py-2.5"
                        >
                            {errMsg}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 text-black font-medium font-inter px-7 py-3.5 rounded-full transition-all duration-200 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Signing in…
                            </>
                        ) : (
                            <>
                                Sign In
                                <ArrowRight size={18} />
                            </>
                        )}
                    </button>
                </form>

                <p className="mt-6 text-center font-inter text-xs text-zinc-600">
                    Internal use only. Unauthorised access is prohibited.
                </p>
            </div>
        </main>
    );
};

export default AdminLoginPage;
